'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { BillingPlans } from '@/features/subscription/billing/billing-plans/billing-plans'
import { BillingCtaProvider } from '@/features/subscription/billing/billing-cta-context'
import { BillingPlansDesignProvider } from '@/features/subscription/billing/billing-plans/billing-plans-design-context'
import { useGetPublicBillingPlansQuery } from '@/api/public/billing/get-public-billing-plans-query'
import { useLandingAuthContext } from '@/features/landing-page/landing-auth-context'
import { UrlConfig } from '@/setup/routing/routing'

/**
 * lp-v3 /pricing plans — the shared BillingPlans component (Starter through Enterprise, live plan
 * data, full badge/CTA/dialog logic) rendered in the redesign skin via BillingPlansDesignProvider.
 * CTA wiring mirrors the live pricing page's PricingBillingPlans: logged-out clicks open the landing
 * auth flow, signed-in clicks route to the dashboard. `showSectionsBelow` is off because the v3 page
 * has its own FAQ section right below.
 *
 * Plan data is client-fetched here (same query key BillingPlans uses internally — TanStack dedupes),
 * purely so this wrapper can show a v3 skeleton while loading and a retry state on failure, since
 * BillingPlans itself renders nothing without data. TODO(landing-launch): seed
 * initialPublicBillingPlans server-side (like the old pricing page does) when the layout gets its
 * per-route v3 slot, so the plans ship in the SSR HTML.
 */
export function LpBillingPlans() {
    const router = useRouter()
    const { handleCtaClick } = useLandingAuthContext()
    const { data, isPending, refetch } = useGetPublicBillingPlansQuery()

    const onUnauthenticatedCtaClick = useCallback(() => {
        handleCtaClick()
    }, [handleCtaClick])

    const onAuthenticatedCtaClick = useCallback(() => {
        router.push(UrlConfig['/routing'].getUrl())
    }, [router])

    if (isPending) return <PlansSkeleton />

    if (!data) {
        return (
            <div className="flex min-h-[300px] flex-col items-center justify-center gap-4">
                <p className="text-[14px]! text-[#746C64]">Couldn&apos;t load the plans.</p>
                <Button variant="lp-secondary" size="lp-lg" onClick={() => refetch()}>
                    Try again
                </Button>
            </div>
        )
    }

    return (
        <BillingPlansDesignProvider value="lpV3">
            <BillingCtaProvider
                onUnauthenticatedCtaClick={onUnauthenticatedCtaClick}
                onAuthenticatedCtaClick={onAuthenticatedCtaClick}
            >
                <BillingPlans showSectionsBelow={false} initialPublicBillingPlans={data} />
            </BillingCtaProvider>
        </BillingPlansDesignProvider>
    )
}

/** Pulsing card outlines in the v3 card geometry so the page doesn't collapse while plans load.
 *  lp-v3 hides the Individual/Professional switcher and the cross-sell text link — the skeleton
 *  matches that shape (five cards: Starter + Advanced + Pro + Scale + Enterprise), so we don't flash a
 *  placeholder for controls that never appear. */
function PlansSkeleton() {
    return (
        <div className="@container w-full">
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 @min-[760px]:grid-cols-3 @min-[1120px]:grid-cols-5">
                {Array.from({ length: 5 }, (_, index) => (
                    <div key={index} className="h-[560px] animate-pulse rounded-[16px] bg-black/[0.04]" />
                ))}
            </div>
        </div>
    )
}
